import { StepEditorProps } from "../useStepEditor";
import { StepEditorAccordion } from "../../../workflows/routes/WorkflowEditor/StepEditorAccordion";
import { Block } from "../../../../components/atoms";
import { useStepOptions } from "../useStepOptions";
import { prefixForm } from "../../../../utils/FormUtils";
import { useMemo } from "react";
import { Field, Option, Select } from "@introist/react-foundation/v2";
import { FieldAwareInput } from "../../../employees/fields/FieldAwareInput";
import { MarkdownEditor } from "../../../../components/editors/v4/MarkdownEditor/MarkdownEditor";

export const LinearIssueStepEditor = ({ form, stepDataPath, workflowId }: StepEditorProps) => {
  const prefixedForm = prefixForm(form, stepDataPath);

  const { options } = useStepOptions(form.data.stepType, {
    teamId: prefixedForm.get("teamId")
  });

  const teamOptions: Option[] = useMemo(() => {
    if (!options || !options.teams) return [];
    return options.teams.map((t: { id: string; name: string }) => ({ key: t.id, title: t.name }));
  }, [options]);

  const projectOptions: Option[] = useMemo(() => {
    if (!options || !options.projects) return [];
    return options.projects.map((p: { id: string; name: string }) => ({ key: p.id, title: p.name }));
  }, [options]);

  const stateOptions: Option[] = useMemo(() => {
    if (!options || !options.states) return [];
    return options.states.map((s: { id: string; name: string }) => ({ key: s.id, title: s.name }));
  }, [options]);

  const priorityOptions: Option[] = useMemo(() => {
    if (!options || !options.priorities) return [];
    return options.priorities.map((p: { value: number; name: string }) => ({
      key: String(p.value),
      title: p.name
    }));
  }, [options]);

  return (
    <>
      <Block blended compact>
        <StepEditorAccordion
          title="Target"
          defaultOpen={!form.data.id}
          description={teamOptions.find(o => o.key === prefixedForm.get("teamId"))?.title}
        >
          <StepEditorAccordion.Row label="Team">
            <Select
              size="small"
              element="button"
              variant="blended"
              style={{ width: "100%" }}
              options={teamOptions}
              value={prefixedForm.get("teamId")}
              onSelect={opt => prefixedForm.set("teamId")(opt.key)}
              placeholder="Select team"
              attachToRef={false}
            />
          </StepEditorAccordion.Row>
          <StepEditorAccordion.Row label="Project (optional)">
            <Select
              size="small"
              element="button"
              variant="blended"
              style={{ width: "100%" }}
              options={projectOptions}
              value={prefixedForm.get("projectId")}
              onSelect={opt => prefixedForm.set("projectId")(opt.key)}
              placeholder="Select project"
              attachToRef={false}
            />
          </StepEditorAccordion.Row>
          <StepEditorAccordion.Row label="Initial status (optional)">
            <Select
              size="small"
              element="button"
              variant="blended"
              style={{ width: "100%" }}
              options={stateOptions}
              value={prefixedForm.get("stateId")}
              onSelect={opt => prefixedForm.set("stateId")(opt.key)}
              placeholder="Select status"
              attachToRef={false}
            />
          </StepEditorAccordion.Row>
          <StepEditorAccordion.Row label="Priority (optional)">
            <Select
              size="small"
              element="button"
              variant="blended"
              style={{ width: "100%" }}
              options={priorityOptions}
              value={
                prefixedForm.get("priority") !== undefined
                  ? String(prefixedForm.get("priority"))
                  : undefined
              }
              onSelect={opt => prefixedForm.set("priority")(Number(opt.key))}
              placeholder="Select priority"
              attachToRef={false}
            />
          </StepEditorAccordion.Row>
        </StepEditorAccordion>
      </Block>
      <Block blended compact>
        <StepEditorAccordion title="Issue">
          <Field title="Title">
            <FieldAwareInput
              value={prefixedForm.get("title")}
              onChange={prefixedForm.set("title")}
              error={prefixedForm.isError("title")}
              placeholder="Issue title"
              size="small"
            />
          </Field>
          <Field title="Description">
            <MarkdownEditor
              content={prefixedForm.get("description") ?? ""}
              onChange={prefixedForm.set("description")}
              placeholder="Issue description"
              error={prefixedForm.isError("description")}
              workflowId={workflowId}
            />
          </Field>
          <Field title="Assignee (optional)">
            <FieldAwareInput
              value={prefixedForm.get("assignee")}
              onChange={prefixedForm.set("assignee")}
              error={prefixedForm.isError("assignee")}
              size="small"
            />
          </Field>
        </StepEditorAccordion>
      </Block>
    </>
  );
};
